import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Loader2, Plus, Shapes, X } from 'lucide-react';

import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';

import {
  crearTipoObjeto,
  type CrearTipoObjetoPayload,
  type TipoObjeto,
} from '@/services/api/inventario.api';
import { getApiErrorMessage } from '@/services/api/client';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (tipo: TipoObjeto) => void;
}

/** Same rule as the backend: trim, collapse whitespace, uppercase. */
function normalize(value: string): string {
  return value.trim().replace(/\s+/g, ' ').toUpperCase();
}

/** Popup to create a new Compras object type (name + optional specifications). */
export function NuevoTipoObjetoDialog({ open, onOpenChange, onCreated }: Props) {
  const qc = useQueryClient();
  const [nombre, setNombre] = useState('');
  const [specInput, setSpecInput] = useState('');
  const [specs, setSpecs] = useState<string[]>([]);

  function reset() {
    setNombre('');
    setSpecInput('');
    setSpecs([]);
  }

  const mutation = useMutation({
    mutationFn: (payload: CrearTipoObjetoPayload) => crearTipoObjeto(payload),
    onSuccess: (tipo) => {
      toast.success('Tipo creado', { description: `${tipo.nombre} ya está disponible.` });
      qc.setQueryData(['tipos-objeto'], (prev: TipoObjeto[] | undefined) =>
        [...(prev ?? []).filter((t) => t.id_tipo !== tipo.id_tipo), tipo].sort((a, b) =>
          a.nombre.localeCompare(b.nombre),
        ),
      );
      void qc.invalidateQueries({ queryKey: ['tipos-objeto'] });
      onCreated(tipo);
      onOpenChange(false);
      reset();
    },
    onError: (err) => {
      toast.error('No se pudo crear el tipo', { description: getApiErrorMessage(err) });
    },
  });

  const nombreNormalizado = normalize(nombre);

  /** Returns the spec list with the pending input merged in (empties/duplicates ignored). */
  function withPendingSpec(current: string[]): string[] {
    const spec = normalize(specInput);
    return spec && !current.includes(spec) ? [...current, spec] : current;
  }

  function addSpec() {
    setSpecs((prev) => withPendingSpec(prev));
    setSpecInput('');
  }

  function handleSubmit() {
    if (!nombreNormalizado || mutation.isPending) return;
    mutation.mutate({ nombre: nombreNormalizado, especificaciones: withPendingSpec(specs) });
  }

  function handleOpenChange(next: boolean) {
    if (mutation.isPending) return;
    if (!next) reset();
    onOpenChange(next);
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-primary-50 text-primary-700">
              <Shapes className="h-4 w-4" />
            </span>
            Nuevo tipo de objeto
          </DialogTitle>
          <DialogDescription>
            Agregalo al catálogo de Compras. Quedará disponible para todos los registros.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4">
          <div className="grid gap-1.5">
            <Label htmlFor="nuevo-tipo-nombre">
              Nombre del tipo <span className="text-danger">*</span>
            </Label>
            <Input
              id="nuevo-tipo-nombre"
              placeholder="Nombre del tipo (ej: Silla)"
              value={nombre}
              maxLength={100}
              autoFocus
              onChange={(e) => setNombre(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleSubmit();
                }
              }}
            />
            {nombreNormalizado && (
              <div className="text-xs text-muted-foreground">
                <p>
                  Se guardará como: <span className="font-medium">{nombreNormalizado}</span>
                </p>
                <p>
                  Serial sugerido:{' '}
                  <span className="font-mono">{nombreNormalizado}-001</span>
                </p>
              </div>
            )}
          </div>

          <div className="grid gap-1.5">
            <Label htmlFor="nuevo-tipo-spec">Especificaciones (opcional)</Label>
            <div className="flex gap-2">
              <Input
                id="nuevo-tipo-spec"
                placeholder="Agregá una especificación (ej: Rimax)"
                value={specInput}
                onChange={(e) => setSpecInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addSpec();
                  }
                }}
              />
              <Button
                type="button"
                variant="outline"
                disabled={!normalize(specInput)}
                onClick={addSpec}
              >
                <Plus className="h-4 w-4" />
                Agregar
              </Button>
            </div>
            {specs.length > 0 ? (
              <div className="flex flex-wrap gap-1.5 pt-1">
                {specs.map((s) => (
                  <Badge key={s} variant="secondary" className="gap-1 pr-1">
                    {s}
                    <button
                      type="button"
                      aria-label={`Quitar ${s}`}
                      className="rounded-full p-0.5 hover:bg-muted-foreground/20"
                      onClick={() => setSpecs((prev) => prev.filter((x) => x !== s))}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">
                Sin especificaciones: al registrar se escribirá libremente.
              </p>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            disabled={mutation.isPending}
            onClick={() => handleOpenChange(false)}
          >
            Cancelar
          </Button>
          <Button type="button" onClick={handleSubmit} disabled={!nombreNormalizado || mutation.isPending}>
            {mutation.isPending ? (
              <>
                <Loader2 className="animate-spin" />
                Creando...
              </>
            ) : (
              'Crear tipo'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
